import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid, Stack, Container, Button } from '@mui/material';
import { Topheader } from '../../components/Topheader';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import TableV2, { spreadPaginationOptions } from '../../components/Table/TableV2';
import usersService from '../../services/usersService';
import locationFeatureFlagService from '../../services/locationFeatureFlagService';
import { ActivateButton, EditButton } from '../../components/Table/components';
import FeatureFlag from '../../components/FeatureFlag/FeatureFlag';
import FeatureFlagButtonDisabler from '../../components/FeatureFlagButtonDisabler/FeatureFlagButtonDisabler';
import config from '../../constants/config';

const ROLES = {
  3: 'Manager',
  2: 'Admin',
  1: 'Super Admin',
  0: 'Sub Admin',
  4: 'Regular User',
  5: 'Customer User',
};

const UsersTable = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isUserAllowedToExport, setIsUserAllowedToExport] = useState(false);
  const [isUserAllowedToChangeStatus, setIsUserAllowedToChangeStatus] = useState(false);
  const [isUserAllowedToEditUser, setIsUserAllowedToEditUser] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isUserAllowedToCreateRegularUser, setIsUserAllowedToCreateRegularUser] = useState(false);
  const [isUserAllowedToCreateManager, setIsUserAllowedToCreateManager] = useState(false);
  const [messages, setMessages] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [paginationOptions, setPaginationOptions] = useState({});
  const [userWriteFlag, setUserWriteFlag] = useState({ action: null });

  useEffect(() => {
    let isActive = true;
    locationFeatureFlagService
      .lookupLocationFeatureFlag({ featureFlag: 'user:write' })
      .then(({ data }) => {
        if (isActive) {
          setUserWriteFlag({ action: data?.action ?? null, warningMessage: data?.warningMessage });
        }
      })
      .catch((error) => {
        console.error('Could not verify user:write feature availability.', error);
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};
    setIsUserAllowedToExport(user?.role <= 1 || !!user?.permissions?.exportRecord);
    setIsUserAllowedToChangeStatus(user?.role <= 3 || !!user?.permissions?.isActiveUser);
    setIsUserAllowedToEditUser(user?.role <= 3 || !!user?.permissions?.editUser);
    setIsManager(user?.role === config.MANAGER_ROLE);
    setIsUserAllowedToCreateRegularUser(user?.role <= 3 && !!user?.permissions?.creatNewUser); // Need both role and permission
    setIsUserAllowedToCreateManager(user?.role <= 2 && !!user?.permissions?.creatNewUser);
  }, []);

  const getUsers = (paginationOptions) => {
    const query = spreadPaginationOptions(paginationOptions);

    setIsLoading(true);
    usersService
      .getUsers(query)
      .then((response) => {
        if (response.data) {
          setUsers(response.data?.users ?? []);
          setRowCount(response.data?.rowCount ?? 0);
        }
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not retrieve users. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchUsers = (newPaginationOptions) => {
    setPaginationOptions(newPaginationOptions);
    getUsers(newPaginationOptions);
  };

  const handleToggleUserStatus = (userId, newStatus) => {
    setIsLoading(true);
    usersService
      .updateUserStatus(userId, newStatus)
      .then(() => {
        getUsers(paginationOptions);
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not update user status. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddRegularUser = () => {
    history.push('/AddUser');
  };

  const handleAddManager = () => {
    history.push('/AddManager');
  };

  const handleEditUser = (userId) => {
    history.push('/EditUser/' + userId);
  };

  const columns = [
    {
      field: 'firstName',
      type: 'string',
      headerName: 'First Name',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'lastName',
      type: 'string',
      headerName: 'Last Name',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'phone',
      type: 'string',
      headerName: 'Phone',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'role',
      type: 'string',
      headerName: 'Role',
      minWidth: 130,
      flex: 1,
      valueFormatter: (value) => ROLES[value],
    },
    {
      field: 'status',
      type: 'string',
      headerName: 'Active Status',
      minWidth: 150,
      flex: 1,
      valueFormatter: (value) => (value ? 'Active' : 'Inactive'),
    },
    {
      field: 'locationNames',
      type: 'string',
      headerName: 'Locations',
      minWidth: 150,
      flex: 2,
      valueFormatter: (value) => (Array.isArray(value) ? value.join(', ') : value),
    },

    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <FeatureFlagButtonDisabler name="user:write" action={userWriteFlag.action}>
          <Stack direction="row" spacing={2}>
            {isUserAllowedToEditUser && (!isManager || params.row.role === config.REGULAR_USER_ROLE) && (
              <EditButton
                id={`edit-${params.row._id}`}
                dataCy={`edit-user-${params.row.lastName}`}
                onClick={() => handleEditUser(params.row._id)}
              />
            )}
            {isUserAllowedToChangeStatus && (
              <ActivateButton
                id={`activate-${params.row._id}`}
                dataCy={`activate-user-${params.row.lastName}`}
                onClick={() => handleToggleUserStatus(params.row._id, !params.row.status)}
                isActive={params.row.status}
                isPerson
              />
            )}
          </Stack>
        </FeatureFlagButtonDisabler>
      ),
    },
  ];

  return (
    <div className="rightPanel">
      <Topheader />
      <div className="contentPart">
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginBottom: '40px' }}
          className="terminal-container term-add"
        >
          <Grid container rowSpacing={2}>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <h3 className="terminal-title">Users</h3>
            </Grid>
            <Grid item xs={12}>
              <FlexAlerts messages={messages} />
              <FeatureFlag
                name="user:write"
                action={userWriteFlag.action}
                warningMessage={userWriteFlag.warningMessage}
              />
            </Grid>
            <Grid item xs={12}>
              <TableV2
                rows={users}
                columns={columns}
                fetchData={fetchUsers}
                rowCount={rowCount}
                isLoading={isLoading}
                isExportable={isUserAllowedToExport}
                searchConfig={{ isSearchBar: true }}
                toolbarExtension={
                  <FeatureFlagButtonDisabler name="user:write" action={userWriteFlag.action}>
                    {isUserAllowedToCreateRegularUser && (
                      <Button id="add-user-button" data-cy="add-user-button" onClick={handleAddRegularUser}>
                        Add User
                      </Button>
                    )}
                    {isUserAllowedToCreateManager && (
                      <Button id="add-manager-button" data-cy="add-manager-button" onClick={handleAddManager}>
                        Add Manager
                      </Button>
                    )}
                  </FeatureFlagButtonDisabler>
                }
              />
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default UsersTable;
